import * as React from "react";
import { createSelector, weakMapMemoize } from "reselect";

import {
  EphemeralWorkspaceState,
  EphemeralWorkspaceStateContext,
} from "@/shared/components/visualization/WorkspaceCtx/EphemeralWorkspaceState";
import { AssociationType } from "@/shared/domain/association.ts";
import { FileRecord } from "@/shared/domain/files";
import { constructTree, TopicNode, TopicRecord } from "@/shared/domain/topics";
import {
  TimeBounds,
  determineTimeBounds,
} from "@/shared/domain/topics/determineTimeBounds";
import { useVizState } from "@/shared/state/visualization";
import { diffVizState } from "@/shared/state/visualization/diffVizState";
import { TimeBoundsSource } from "@/shared/state/visualization/schema/v1";

import { MatchError } from "../layouts/LayoutToVizConfig";

export const useEphemeralWorkspaceState = (): EphemeralWorkspaceState => {
  return React.useContext(EphemeralWorkspaceStateContext);
};

type EphemeralPanelStateMap = Map<string, Record<string, unknown>>;
type UpdateEphemeralPanelState = (
  panelId: string,
  updates: Record<string, unknown>,
) => void;

export const useEphemeralPanelState = (): [
  EphemeralPanelStateMap,
  UpdateEphemeralPanelState,
] => {
  const { ephemeralPanelState, updateEphemeralPanelState } =
    useEphemeralWorkspaceState();
  return [ephemeralPanelState, updateEphemeralPanelState];
};

const selectDatasetIdsForFiles =
  createSelector.withTypes<EphemeralWorkspaceState>()(
    [(state) => state, (_, fileIds: string[]) => fileIds],
    (state, fileIds) => {
      return state.files
        .filter((file) => fileIds.includes(file.file_id))
        .map((file) => file.association_id);
    },
    {
      memoizeOptions: {
        resultEqualityCheck(a: string[], b: string[]) {
          if (a.length !== b.length) {
            return false;
          }
          for (let i = 0; i < a.length; i++) {
            if (a[i] !== b[i]) {
              return false;
            }
          }

          return true;
        },
      },
    },
  );

const selectFilesForTopics =
  createSelector.withTypes<EphemeralWorkspaceState>()(
    [(state) => state, (_, topicIds: string[]) => topicIds],
    (state, topicIds) => {
      const fileIds: string[] = state.topics
        .filter(
          (topic) =>
            topic.association.association_type === AssociationType.File &&
            topicIds.includes(topic.topic_id),
        )
        .map((topic) => topic.association.association_id);

      return state.files.filter((file) => fileIds.includes(file.file_id));
    },
    {
      memoizeOptions: {
        resultEqualityCheck(a: FileRecord[], b: FileRecord[]) {
          if (a.length !== b.length) {
            return false;
          }
          for (let i = 0; i < a.length; i++) {
            if (a[i].file_id !== b[i].file_id) {
              return false;
            }
          }

          return true;
        },
      },
    },
  );

const selectTopicsForFile = createSelector.withTypes<EphemeralWorkspaceState>()(
  [(state) => state, (_, fileId: string) => fileId],
  (state, fileId) => {
    return state.topics.filter(
      (topic) => fileId === topic.association.association_id,
    );
  },
  {
    memoizeOptions: {
      resultEqualityCheck(a: TopicRecord[], b: TopicRecord[]) {
        if (a.length !== b.length) {
          return false;
        }
        for (let i = 0; i < a.length; i++) {
          if (a[i].topic_id !== b[i].topic_id) {
            return false;
          }
        }

        return true;
      },
    },
    memoize: weakMapMemoize,
    argsMemoize: weakMapMemoize,
  },
);

const constructTreeForTopics =
  createSelector.withTypes<EphemeralWorkspaceState>()(
    [selectTopicsForFile],
    (topics: TopicRecord[]) => {
      return constructTree(topics);
    },
    {
      memoizeOptions: {
        resultEqualityCheck(a: TopicNode[], b: TopicNode[]) {
          if (a.length !== b.length) {
            return false;
          }
          for (let i = 0; i < a.length; i++) {
            if (a[i].id !== b[i].id) {
              return false;
            }
          }

          return true;
        },
      },
      memoize: weakMapMemoize,
      argsMemoize: weakMapMemoize,
    },
  );

const selectTimeBoundsForTopics =
  createSelector.withTypes<EphemeralWorkspaceState>()(
    [(state) => state.topics, (_, options: TimeBoundsSourceOptions) => options],
    (topics, options): TimeBounds => {
      let idSet: Set<string>;
      switch (options.timeBoundsSource) {
        case TimeBoundsSource.FilesInWorkspace:
          return determineTimeBounds(topics);
        case TimeBoundsSource.FilesInPanel:
          idSet = new Set(options.fileIds);
          return determineTimeBounds(
            topics.filter((topic) =>
              idSet.has(topic.association.association_id),
            ),
          );
        case TimeBoundsSource.TopicsInPanel:
          idSet = new Set(options.topicIds);
          return determineTimeBounds(
            topics.filter((topic) => idSet.has(topic.topic_id)),
          );
      }
    },
    {
      memoizeOptions: {
        resultEqualityCheck(a: TimeBounds, b: TimeBounds) {
          return a.earliest === b.earliest && a.latest === b.latest;
        },
      },
      memoize: weakMapMemoize,
      argsMemoize: weakMapMemoize,
    },
  );

export const useTopicTreeForFile = (fileId?: string): TopicNode[] => {
  const state = useEphemeralWorkspaceState();

  if (fileId === undefined) {
    return [];
  }

  return constructTreeForTopics(state, fileId);
};

export const selectAllWorkspaceTopicTrees =
  createSelector.withTypes<EphemeralWorkspaceState>()(
    [(state) => state],
    (state) => {
      const fileIds = state.files.map((f) => f.file_id);
      return fileIds.flatMap((fileId) => constructTreeForTopics(state, fileId));
    },
    {
      memoizeOptions: {
        resultEqualityCheck(a: TopicNode[], b: TopicNode[]) {
          if (a.length !== b.length) {
            return false;
          }
          for (let i = 0; i < a.length; i++) {
            if (a[i].id !== b[i].id) {
              return false;
            }
          }

          return true;
        },
      },
      memoize: weakMapMemoize,
      argsMemoize: weakMapMemoize,
    },
  );

export type TimeBoundsSourceOptions =
  | { timeBoundsSource: TimeBoundsSource.FilesInWorkspace }
  | { timeBoundsSource: TimeBoundsSource.FilesInPanel; fileIds: string[] }
  | { timeBoundsSource: TimeBoundsSource.TopicsInPanel; topicIds: string[] };

export const useWorkspaceTimeBounds = (
  options: TimeBoundsSourceOptions,
): TimeBounds => {
  const state = useEphemeralWorkspaceState();
  return selectTimeBoundsForTopics(state, options);
};

export const useWorkspaceTopicsForFile = (fileId?: string): TopicRecord[] => {
  const state = useEphemeralWorkspaceState();

  if (fileId === undefined) {
    return [];
  }

  return selectTopicsForFile(state, fileId);
};

export const useWorkspaceDatasetIdsForFiles = (fileIds: string[]) => {
  const state = useEphemeralWorkspaceState();
  return selectDatasetIdsForFiles(state, fileIds);
};

export const useWorkspaceFiles = (): FileRecord[] => {
  const state = useEphemeralWorkspaceState();
  return state.files;
};

/**
 * Workspace files filtered to those that contain at least one topic — i.e.,
 * files usable as data sources. Excludes media (images, videos, etc.) and
 * any file whose topics haven't been ingested.
 */
export const useDataFiles = (): FileRecord[] => {
  const files = useWorkspaceFiles();
  const fileIdToTopics = useFileIdToTopics();
  return React.useMemo(
    () => files.filter((f) => (fileIdToTopics[f.file_id] ?? []).length > 0),
    [files, fileIdToTopics],
  );
};

export const useWorkspaceFilesForTopics = (
  topicIds: string[],
): FileRecord[] => {
  const state = useEphemeralWorkspaceState();
  return selectFilesForTopics(state, topicIds);
};

export const useWorkspaceMessagePathById = (messagePathId?: string) => {
  const state = useEphemeralWorkspaceState();

  if (messagePathId === undefined) {
    return undefined;
  }

  return state.messagePaths.find(
    (messagePath) => messagePath.message_path_id === messagePathId,
  );
};

export const useWorkspaceTopicById = (topicId?: string) => {
  const state = useEphemeralWorkspaceState();

  if (topicId === undefined) {
    return undefined;
  }

  return state.topics.find((topic) => topic.topic_id === topicId);
};

export const useWorkspaceTopicByMessagePathId = (messagePathId?: string) => {
  const state = useEphemeralWorkspaceState();
  const messagePath = useWorkspaceMessagePathById(messagePathId);

  if (messagePath === undefined) {
    return undefined;
  }

  return state.topics.find((topic) => topic.topic_id === messagePath.topic_id);
};

export const useEphemeralWorkspaceStateLoading = () => {
  const state = useEphemeralWorkspaceState();
  return state.isDataLoading;
};

export const useWorkspaceError = () => {
  const state = useEphemeralWorkspaceState();
  return state.error;
};

export const useSelectedPanelId = () => {
  const state = useEphemeralWorkspaceState();
  return state.selectedPanelId;
};

export const useSelectPanel = (): EphemeralWorkspaceState["selectPanel"] => {
  const state = useEphemeralWorkspaceState();
  return state.selectPanel;
};

export const useWorkspaceTimer = () => {
  const state = useEphemeralWorkspaceState();
  return state.timer;
};

export function useTimeDisplayMode(): [
  EphemeralWorkspaceState["timeDisplayMode"],
  EphemeralWorkspaceState["setTimeDisplayMode"],
] {
  const { timeDisplayMode, setTimeDisplayMode } = useEphemeralWorkspaceState();
  return [timeDisplayMode, setTimeDisplayMode];
}

export function useActiveLayout(): [
  EphemeralWorkspaceState["activeLayout"],
  EphemeralWorkspaceState["setActiveLayout"],
] {
  const { activeLayout, setActiveLayout } = useEphemeralWorkspaceState();
  return [activeLayout, setActiveLayout];
}

export function useDataNotFoundErrors(
  panelId: string,
): MatchError[] | undefined {
  const { dataMatchingErrors } = useEphemeralWorkspaceState();
  return dataMatchingErrors[panelId];
}

export function useFileIdToTopics() {
  const workspaceState = useEphemeralWorkspaceState();
  const allTopics = selectAllWorkspaceTopicTrees(workspaceState);
  const fileIdToTopics: Record<string, TopicNode[]> = React.useMemo(() => {
    const fileIdToTopics: Record<string, TopicNode[]> = {};
    for (const t of allTopics) {
      fileIdToTopics[t.data.association.association_id] ??= [];
      fileIdToTopics[t.data.association.association_id].push(t);
    }
    return fileIdToTopics;
  }, [allTopics]);
  return fileIdToTopics;
}

export function useWorkspaceSnapshot(): [
  EphemeralWorkspaceState["workspaceSnapshot"],
  EphemeralWorkspaceState["setWorkspaceSnapshot"],
] {
  const { workspaceSnapshot, setWorkspaceSnapshot } =
    useEphemeralWorkspaceState();
  return [workspaceSnapshot, setWorkspaceSnapshot];
}

export function useUnsavedChanges() {
  const [initialState] = useWorkspaceSnapshot();
  const currentState = useVizState();

  const hasUnsavedChanges = React.useCallback(() => {
    const diff = diffVizState(initialState, currentState);
    return diff.hasChanges;
  }, [initialState, currentState]);

  return {
    hasUnsavedChanges,
  };
}
